<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { useAuthStore } from '@/stores/auth';
import { useRowDelete } from '@/composables/useRowDelete';
import {
  type PromptInDb,
  type PromptUpdate,
  type PromptCollectionInDb,
  type EnumResponseType,
  type EnumResponseTypeValue,
  getSummaryFileLevelDefault,
} from '@/api';
import AgentEditDrawer from '@/components/AgentEditDrawer.vue';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  gaiaAccent,
  gaiaAmber,
  gaiaContentWidth,
  gaiaFonts,
  gaiaFontSize,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';

/**
 * One prompt collection — its prompts (Prompt Collections mock, detail board).
 * The hero names the collection and carries its counts and dates; the table
 * below is the prompt inventory: mono names, type, a clamped description, the
 * choice options, and which agents run. Editing goes through AgentEditDrawer,
 * unchanged.
 */

const route = useRoute();
const router = useRouter();
const store = usePromptCollectionsStore();
const message = useGaiaToast();
const authStore = useAuthStore();

const { currentCollection, currentPrompts, isLoading } = storeToRefs(store);
const collectionId = computed(() => route.params.id as string);

const isOrgAdmin = computed(() => authStore.userPermissions.is_org_admin);

const rowDelete = useRowDelete<PromptInDb>();

// Agent drawer state
const showAgentDrawer = ref<boolean>(false);
const currentAgentPrompt = ref<PromptInDb | null>(null);
const isNewPrompt = ref<boolean>(false);

// Summary prompt modal state
const showSummaryPromptModal = ref<boolean>(false);
const summaryPromptText = ref<string>('');
const isUpdatingSummaryPrompt = ref<boolean>(false);

// Duplicate prompt modal state
const showDuplicateModal = ref<boolean>(false);
const promptToDuplicate = ref<PromptInDb | null>(null);
const availableCollections = ref<{ uid: string; name: string }[]>([]);
const selectedTargetCollection = ref<string>('');
const isDuplicating = ref<boolean>(false);
const targetCollectionOptions = computed<{ label: string; value: string }[]>(() =>
  availableCollections.value.map(col => ({ label: col.name, value: col.uid }))
);

// Description modal state
const showDescriptionModal = ref<boolean>(false);
const selectedPromptDescription = ref<string>('');
const selectedPromptName = ref<string>('');
const selectedPromptExampleData = ref<string[] | null>(null);

// Options modal state
const optionsModalVisible = ref<boolean>(false);
const currentOptions = ref<EnumResponseTypeValue[]>([]);
const isMultipleChoice = ref<boolean>(false);

// Available type options
const typeOptions = [
  { label: 'Text', value: 'str' },
  { label: 'List of Text', value: 'list[str]' },
  { label: 'Number', value: 'float' },
  { label: 'List of Numbers', value: 'list[float]' },
  { label: 'Yes/No', value: 'bool' },
  { label: 'List of Yes/No', value: 'list[bool]' },
  { label: 'Date', value: 'date' },
  { label: 'List of Dates', value: 'list[date]' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'List of Date & Time', value: 'list[datetime]' },
  { label: 'Choice (Single)', value: 'enum' },
  { label: 'Choice (Multiple)', value: 'multiple_enum' },
  { label: 'Number with Currency', value: 'CurrencyAmount' },
  { label: 'List of Numbers with Currency', value: 'list[CurrencyAmount]' },
  { label: 'Person or Organization with Address', value: 'Entity' },
  { label: 'List of Organizations or Persons with Address', value: 'list[Entity]' },
  { label: 'Calendar Schedules', value: 'CalendarEvents' },
];

// --- derived --------------------------------------------------------------------
// Client-side prompt filter and sort — the collection's prompts are all loaded.
const promptSearchTerm = ref<string>('');
const nameSort = ref<'asc' | 'desc' | null>(null);

const filteredPrompts = computed<PromptInDb[]>(() => {
  const term = promptSearchTerm.value.trim().toLowerCase();
  let list = term
    ? currentPrompts.value.filter(
        (p: PromptInDb) =>
          p.name.toLowerCase().includes(term) || p.prompt.toLowerCase().includes(term)
      )
    : currentPrompts.value;
  if (nameSort.value) {
    const direction = nameSort.value === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => direction * a.name.localeCompare(b.name));
  }
  return list;
});

const hasQuery = computed(() => promptSearchTerm.value.trim().length > 0);

const promptCountLabel = computed(() => {
  const count = currentPrompts.value.length;
  return `${count} ${count === 1 ? 'prompt' : 'prompts'}`;
});

const showEmpty = computed(
  () => !isLoading.value && filteredPrompts.value.length === 0 && !hasQuery.value
);
const showNoMatch = computed(
  () => !isLoading.value && filteredPrompts.value.length === 0 && hasQuery.value
);

const skeletonCells = [
  '70%',
  '48px',
  { width: '86%', soft: true },
  { shape: 'chip' },
  { width: '48px' },
] as const;

const tableColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'Name', width: 'minmax(160px,1fr)', sortable: true },
  { key: 'type', label: 'Type', width: '96px' },
  { key: 'prompt', label: 'Description', width: 'minmax(220px,2.2fr)' },
  { key: 'options', label: 'Options', width: '130px' },
  { key: 'agents', label: 'Agents', width: '86px' },
];

const emptyCell = '—';

function formatDate(raw: string | null | undefined): string {
  if (!raw) return emptyCell;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return emptyCell;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function typeLabel(row: PromptInDb): string {
  if (typeof row.response_type === 'object') {
    return row.response_type.multiple_choices ? 'Multiple Choice' : 'Choice';
  }
  const typeOption = typeOptions.find(opt => opt.value === row.response_type);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  return typeOption?.label || row.response_type;
}

function optionsOf(row: PromptInDb): EnumResponseTypeValue[] {
  if (
    typeof row.response_type === 'object' &&
    'values' in row.response_type &&
    Array.isArray(row.response_type.values)
  ) {
    return row.response_type.values;
  }
  return [];
}

/** Which agents run for this prompt — extraction always, the rest when configured. */
function agentsOf(row: PromptInDb): ('extraction' | 'evaluation' | 'suggestion')[] {
  const agents: ('extraction' | 'evaluation' | 'suggestion')[] = ['extraction'];
  if (row.evaluation_prompt) agents.push('evaluation');
  if (row.suggestion_prompt) agents.push('suggestion');
  return agents;
}

const AGENT_TITLES: Record<'extraction' | 'evaluation' | 'suggestion', string> = {
  extraction: 'Extraction agent',
  evaluation: 'Evaluation agent',
  suggestion: 'Suggestion agent',
};

function onSort(key: string): void {
  if (key !== 'name') return;
  nameSort.value = nameSort.value === 'asc' ? 'desc' : 'asc';
}

// --- data -----------------------------------------------------------------------
onMounted(async (): Promise<void> => {
  isLoading.value = true;
  try {
    await store.fetchCollectionWithPrompts(collectionId.value);
    await fetchAllCollections();
  } catch (error) {
    message.error(`Error loading collection: ${error}`);
  }
});

// Fetch all collections for duplicate functionality
async function fetchAllCollections(): Promise<void> {
  try {
    await store.fetchCollections({
      search_term: '',
      page: 1,
      page_size: 50,
    });
    const { collections } = storeToRefs(store);
    availableCollections.value = collections.value.map((col: PromptCollectionInDb) => ({
      uid: col.uid,
      name: col.name,
    }));
  } catch (error) {
    logger.error('Error fetching collections:', error);
  }
}

function backToList(): void {
  void router.push('/prompt-collections');
}

// Opens the read-only description modal for a prompt (also triggered by row click)
function openDescriptionModal(row: PromptInDb): void {
  selectedPromptDescription.value = row.prompt;
  selectedPromptName.value = row.name;
  selectedPromptExampleData.value = row.example_data ?? null;
  showDescriptionModal.value = true;
}

// Per-row "⋯" actions menu — same pattern as the views list table
const promptActionOptions = [
  { label: 'Edit', key: 'edit' },
  { label: 'Duplicate', key: 'duplicate' },
  { key: 'delete-divider', type: 'divider' },
  { label: 'Delete', key: 'delete', props: gaiaDangerOption },
];

function handlePromptActionSelect(key: string | number, row: PromptInDb): void {
  switch (key) {
    case 'edit':
      handleEditPrompt(row);
      break;
    case 'duplicate':
      handleDuplicatePrompt(row);
      break;
    case 'delete':
      rowDelete.arm(row);
      break;
  }
}

// --- prompt CRUD -------------------------------------------------------------------
function handleCreatePrompt(): void {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to create prompts.');
    return;
  }
  isNewPrompt.value = true;
  currentAgentPrompt.value = {
    uid: '',
    created_at: new Date().toISOString(),
    name: '',
    prompt: '',
    response_type: 'str',
    example_data: null,
    evaluation_prompt: null,
    evaluation_response_type: null,
    suggestion_prompt: null,
    suggestion_example_data: null,
  };
  showAgentDrawer.value = true;
}

function handleEditPrompt(prompt: PromptInDb): void {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to update prompts.');
    return;
  }
  isNewPrompt.value = false;
  currentAgentPrompt.value = prompt;
  showAgentDrawer.value = true;
}

async function handleSaveChanges(promptId: string, updates: PromptUpdate): Promise<void> {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to update prompts.');
    return;
  }
  try {
    if (isNewPrompt.value) {
      const newPrompt = {
        name: updates.name ?? '',
        prompt: updates.prompt || '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        response_type: updates.response_type || 'str',
        example_data: updates.example_data === undefined ? null : updates.example_data,
        evaluation_prompt: updates.evaluation_prompt,
        evaluation_response_type: updates.evaluation_response_type,
        suggestion_prompt: updates.suggestion_prompt,
        suggestion_example_data: updates.suggestion_example_data,
      };

      const createdPrompt = await store.addPrompt(collectionId.value, newPrompt);
      message.success('Prompt created successfully');

      if (createdPrompt) {
        isNewPrompt.value = false;
        currentAgentPrompt.value = createdPrompt;
      }
    } else {
      await store.updatePrompt(collectionId.value, promptId, updates);
      message.success('Prompt updated successfully');
    }

    // Refetch prompts to ensure correct order
    await store.fetchCollectionWithPrompts(collectionId.value);
  } catch (error) {
    message.error(isNewPrompt.value ? 'Failed to create prompt' : 'Failed to update prompt');
    logger.error(isNewPrompt.value ? 'Failed to create prompt' : 'Failed to update prompt', error);
  }
}

async function deletePrompt(prompt: PromptInDb): Promise<void> {
  await rowDelete.run(prompt, {
    work: async () => {
      await store.deletePrompt(collectionId.value, prompt.uid);
      message.success('Prompt deleted');
    },
    errorText: () => 'Couldn’t delete this prompt. Nothing was changed.',
  });
}

// --- duplicate ---------------------------------------------------------------------
function handleDuplicatePrompt(prompt: PromptInDb): void {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to duplicate prompts.');
    return;
  }
  promptToDuplicate.value = prompt;
  selectedTargetCollection.value = collectionId.value; // Default to current collection
  showDuplicateModal.value = true;
}

async function executeDuplicatePrompt(): Promise<void> {
  if (!promptToDuplicate.value || !selectedTargetCollection.value) {
    message.error('Missing prompt or target collection');
    return;
  }

  isDuplicating.value = true;

  try {
    await store.addPrompt(selectedTargetCollection.value, {
      name: promptToDuplicate.value.name,
      prompt: promptToDuplicate.value.prompt,
      response_type: promptToDuplicate.value.response_type,
      example_data: promptToDuplicate.value.example_data,
      evaluation_prompt: promptToDuplicate.value.evaluation_prompt,
      evaluation_response_type: promptToDuplicate.value.evaluation_response_type,
      suggestion_prompt: promptToDuplicate.value.suggestion_prompt,
      suggestion_example_data: promptToDuplicate.value.suggestion_example_data,
    });

    message.success('Prompt duplicated successfully');
    showDuplicateModal.value = false;
    promptToDuplicate.value = null;
    selectedTargetCollection.value = '';
  } catch (error) {
    logger.error('Error duplicating prompt:', error);
    message.error('Failed to duplicate prompt');
  } finally {
    isDuplicating.value = false;
  }
}

function openOptionsModal(responseType: EnumResponseType): void {
  currentOptions.value = responseType.values;
  isMultipleChoice.value = responseType.multiple_choices;
  optionsModalVisible.value = true;
}

// --- summary prompt ----------------------------------------------------------------
function openSummaryPromptModal(): void {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to update summary prompts.');
    return;
  }
  summaryPromptText.value = currentCollection.value?.summary_prompt ?? '';
  showSummaryPromptModal.value = true;
}

async function fetchDefaultSummaryPrompt(): Promise<void> {
  try {
    const response = await getSummaryFileLevelDefault();
    if (!response.data) {
      throw new Error('Failed to get summary file level default');
    }
    summaryPromptText.value = response.data;
    message.success('Default summary prompt loaded');
  } catch (error) {
    message.error('Failed to load default summary prompt');
    logger.error('Failed to load default summary prompt', error);
  }
}

async function saveSummaryPrompt(): Promise<void> {
  if (!currentCollection.value) return;

  isUpdatingSummaryPrompt.value = true;
  try {
    await store.updateCollection(currentCollection.value.uid, {
      summary_prompt: summaryPromptText.value || null,
    });
    message.success('Summary prompt updated successfully');
    showSummaryPromptModal.value = false;
  } catch (error) {
    message.error('Failed to update summary prompt');
    logger.error('Failed to update summary prompt', error);
  } finally {
    isUpdatingSummaryPrompt.value = false;
  }
}

// --- chrome --------------------------------------------------------------------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceSoft;
const monoFont = gaiaFonts.mono;
const nameColor = gaiaNeutral.ink;
const nameSize = gaiaFontSize.meta;
const chipTextSize = gaiaFontSize.nano;
const chipBorder = gaiaNeutral.borderMuted;
const chipSurface = gaiaNeutral.surfaceTint;
const chipColor = gaiaNeutral.text;
const agentTiles = {
  extraction: {
    bg: 'rgba(42,80,196,.08)',
    border: 'rgba(42,80,196,.18)',
    fg: gaiaStatus.info,
  },
  evaluation: {
    bg: gaiaAccent.wash,
    border: gaiaAccent.border,
    fg: gaiaAccent.base,
  },
  suggestion: {
    bg: gaiaAmber.surface,
    border: gaiaAmber.border,
    fg: gaiaAmber.strong,
  },
} as const;

/**
 * Inline custom properties on the modal cards rather than `v-bind()` — modal
 * roots are Teleports, so Vue has no root element in place to write `v-bind()`
 * vars onto, and values bound that way silently resolve to nothing.
 */
const modalStyleVars = {
  '--pc-border': gaiaNeutral.borderSubtle,
  '--pc-mono': gaiaFonts.mono,
  '--pc-mono-size': gaiaFontSize.micro,
  '--pc-surface': gaiaNeutral.surfaceSoft,
  '--pc-text': gaiaNeutral.textSoft,
};
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link to="/prompt-collections">{{ navLabels.promptLibrary }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ currentCollection?.name ?? '…' }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="16">
      <!-- Collection gone (deleted, or a stale link): the card takes over. -->
      <gaia-empty-state
        v-if="!isLoading && !currentCollection"
        status="alert"
        title="Collection not found"
      >
        It may have been deleted, or the link is out of date.
        <template #actions>
          <gaia-button size="small" @click="backToList">Back to collections</gaia-button>
        </template>
      </gaia-empty-state>

      <template v-else>
        <!-- The collection: name, counts, dates; the two actions pushed right. -->
        <n-flex align="flex-start" :size="[20, 12]">
          <n-flex vertical :size="6" :style="{ flex: '1 1 340px', minWidth: 0 }">
            <gaia-header :level="1" variant="display" size="record" :style="{ margin: 0 }">
              {{ currentCollection?.name ?? '…' }}
            </gaia-header>
            <gaia-text v-if="currentCollection" size="meta" tone="subtle">
              {{ promptCountLabel }} · Modified
              {{ formatDate(currentCollection.updated_at ?? currentCollection.created_at) }} ·
              Created {{ formatDate(currentCollection.created_at) }}
            </gaia-text>
          </n-flex>

          <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
            <gaia-button size="medium" :disabled="!isOrgAdmin" @click="openSummaryPromptModal">
              <template #icon>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 13l1-3.5L11 2.5l2 2L6.5 12z" />
                </svg>
              </template>
              Edit summary prompt
            </gaia-button>
            <gaia-button
              type="primary"
              size="medium"
              :disabled="!isOrgAdmin"
              @click="handleCreatePrompt"
            >
              <template #icon>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </template>
              Add prompt
            </gaia-button>
          </n-flex>
        </n-flex>

        <n-flex align="center" :size="10">
          <gaia-search-input
            v-model:value="promptSearchTerm"
            placeholder="Search prompts"
            :debounce="200"
          />
          <gaia-count-label :style="{ marginLeft: 'auto' }">
            {{ promptCountLabel }}
          </gaia-count-label>
        </n-flex>

        <gaia-data-table
          :columns="tableColumns"
          sort-key="name"
          :sort-direction="nameSort ?? 'asc'"
          :sort-active="nameSort !== null"
          :action-width="44"
          @sort="onSort"
        >
          <template #default="{ rowStyle }">
            <!-- Loading: the toolbar and header stay live, the body shimmers. -->
            <gaia-table-skeleton
              v-if="isLoading"
              :row-style="rowStyle"
              :cells="skeletonCells"
              :rows="6"
            />

            <gaia-empty-state v-else-if="showEmpty" title="No prompts in this collection yet">
              <template #icon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M4 2.5h5L12.5 6v7.5H4z" />
                  <path d="M6 7.5h4M6 10h2.5" />
                </svg>
              </template>
              Each prompt is one question a review asks of a document. Add the first one to give
              this collection a job.
              <template v-if="isOrgAdmin" #actions>
                <gaia-button type="primary" size="small" @click="handleCreatePrompt">
                  Add prompt
                </gaia-button>
              </template>
            </gaia-empty-state>

            <gaia-empty-state
              v-else-if="showNoMatch"
              :title="`No prompt matches “${promptSearchTerm.trim()}”`"
            >
              Search covers prompt names and their descriptions.
              <template #actions>
                <gaia-button size="small" @click="promptSearchTerm = ''">Clear search</gaia-button>
              </template>
            </gaia-empty-state>

            <template v-else>
              <template v-for="prompt in filteredPrompts" :key="prompt.uid">
                <!-- A row in trouble is replaced by its strip, never stacked with it. -->
                <gaia-table-row-state
                  v-if="rowDelete.stateOf(prompt.uid) === 'confirm'"
                  variant="confirm"
                  :title="`Delete “${prompt.name}”?`"
                  @cancel="rowDelete.cancel()"
                  @confirm="deletePrompt(prompt)"
                >
                  Reviews already run with it keep their results.
                </gaia-table-row-state>

                <gaia-table-row-state
                  v-else-if="rowDelete.stateOf(prompt.uid) === 'busy'"
                  variant="busy"
                  note="Undo not available"
                >
                  Deleting “{{ prompt.name }}”…
                </gaia-table-row-state>

                <gaia-table-row-state
                  v-else-if="rowDelete.stateOf(prompt.uid) === 'error'"
                  variant="error"
                  @retry="deletePrompt(prompt)"
                  @dismiss="rowDelete.dismissError()"
                >
                  {{ rowDelete.errorTextOf(prompt.uid) }}
                </gaia-table-row-state>

                <div v-else class="row" :style="rowStyle" @click="openDescriptionModal(prompt)">
                  <div class="cell name-cell">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                      class="name-icon"
                    >
                      <path d="M4 2.5h5L12.5 6v7.5H4z" />
                      <path d="M6 7.5h4M6 10h2.5" />
                    </svg>
                    <span class="prompt-name">{{ prompt.name }}</span>
                  </div>

                  <div class="cell">
                    <gaia-text size="meta" tone="text" :style="{ whiteSpace: 'nowrap' }">
                      {{ typeLabel(prompt) }}
                    </gaia-text>
                  </div>

                  <div class="cell">
                    <gaia-text size="meta" tone="text" class="clamp">{{ prompt.prompt }}</gaia-text>
                  </div>

                  <div class="cell options-cell">
                    <template v-if="optionsOf(prompt).length > 0">
                      <span
                        v-for="option in optionsOf(prompt).slice(0, 2)"
                        :key="option.name"
                        class="option-chip"
                        @click.stop="openOptionsModal(prompt.response_type as EnumResponseType)"
                      >
                        {{ option.name }}
                      </span>
                      <span
                        v-if="optionsOf(prompt).length > 2"
                        class="option-chip"
                        @click.stop="openOptionsModal(prompt.response_type as EnumResponseType)"
                      >
                        +{{ optionsOf(prompt).length - 2 }}
                      </span>
                    </template>
                    <gaia-text v-else size="meta" tone="blank">No options</gaia-text>
                  </div>

                  <div class="cell agents-cell">
                    <gaia-tooltip v-for="agent in agentsOf(prompt)" :key="agent" placement="top">
                      <template #trigger>
                        <span
                          class="agent-tile"
                          :style="{
                            background: agentTiles[agent].bg,
                            borderColor: agentTiles[agent].border,
                            color: agentTiles[agent].fg,
                          }"
                        >
                          <svg
                            v-if="agent === 'extraction'"
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                          >
                            <circle cx="7" cy="7" r="3.6" />
                            <path d="M10 10l3 3" />
                          </svg>
                          <svg
                            v-else-if="agent === 'evaluation'"
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M8 2.2 13 4v4.2c0 3-2.1 4.8-5 5.6-2.9-.8-5-2.6-5-5.6V4z" />
                          </svg>
                          <svg
                            v-else
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M8 2.2 9.4 6 13 7.4 9.4 8.8 8 12.6 6.6 8.8 3 7.4 6.6 6z" />
                          </svg>
                        </span>
                      </template>
                      {{ AGENT_TITLES[agent] }}
                    </gaia-tooltip>
                  </div>

                  <n-flex v-if="isOrgAdmin" justify="end" :size="0" :wrap="false">
                    <gaia-dropdown
                      :options="promptActionOptions"
                      placement="bottom-end"
                      @select="(key: string) => handlePromptActionSelect(key, prompt)"
                    >
                      <gaia-icon-button :dimension="26" quaternary title="More" @click.stop>
                        ⋯
                      </gaia-icon-button>
                    </gaia-dropdown>
                  </n-flex>
                </div>
              </template>
            </template>
          </template>
        </gaia-data-table>
      </template>
    </n-flex>

    <!-- Agent Edit Drawer -->
    <AgentEditDrawer
      v-model:show="showAgentDrawer"
      :prompt="currentAgentPrompt"
      :is-new="isNewPrompt"
      :collection-id="collectionId"
      @save="handleSaveChanges"
    />

    <!-- Options -->
    <gaia-modal
      :show="optionsModalVisible"
      width="520px"
      @update:show="(value: boolean) => (optionsModalVisible = value)"
    >
      <template #header>
        <n-flex vertical :size="2">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Options</gaia-text>
          <gaia-text size="meta" tone="subtle">
            {{
              isMultipleChoice
                ? 'AI can select multiple options from the list.'
                : 'AI must select exactly one option from the list.'
            }}
          </gaia-text>
        </n-flex>
      </template>

      <n-flex vertical :size="10">
        <n-flex
          v-for="(option, index) in currentOptions"
          :key="index"
          vertical
          :size="3"
          class="option-row"
        >
          <gaia-text size="control" weight="semibold" tone="ink">{{ option.name }}</gaia-text>
          <gaia-text v-if="option.definition" size="meta" tone="tertiary">
            {{ option.definition }}
          </gaia-text>
        </n-flex>
      </n-flex>

      <template #footer>
        <n-flex justify="end">
          <gaia-button size="small" @click="optionsModalVisible = false">Close</gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Summary prompt -->
    <gaia-modal
      :show="showSummaryPromptModal"
      width="720px"
      @update:show="(value: boolean) => (showSummaryPromptModal = value)"
    >
      <template #header>
        <n-flex vertical :size="2">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Summary prompt</gaia-text>
          <gaia-text size="meta" tone="subtle">
            Runs after every prompt in the collection has executed, receiving all their results, and
            writes the final summary.
          </gaia-text>
        </n-flex>
      </template>

      <gaia-textarea
        v-model:value="summaryPromptText"
        placeholder="Enter your summary prompt here…"
        :autosize="{ minRows: 12, maxRows: 22 }"
        style="width: 100%"
      />

      <template #footer>
        <n-flex align="center" :size="8">
          <gaia-button
            size="small"
            :loading="isUpdatingSummaryPrompt"
            @click="fetchDefaultSummaryPrompt"
          >
            Insert Gaia default
          </gaia-button>
          <n-flex justify="end" :size="8" :style="{ marginLeft: 'auto' }">
            <gaia-button size="small" @click="showSummaryPromptModal = false">Cancel</gaia-button>
            <gaia-button
              type="primary"
              size="small"
              :disabled="!isOrgAdmin"
              :loading="isUpdatingSummaryPrompt"
              @click="saveSummaryPrompt"
            >
              Save
            </gaia-button>
          </n-flex>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Duplicate prompt -->
    <gaia-modal
      :show="showDuplicateModal"
      width="480px"
      @update:show="(value: boolean) => (showDuplicateModal = value)"
    >
      <template #header>
        <n-flex vertical :size="2">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Duplicate prompt</gaia-text>
          <gaia-text v-if="promptToDuplicate" size="meta" tone="subtle">
            Copies “{{ promptToDuplicate.name }}” with all its settings and evaluation
            configuration.
          </gaia-text>
        </n-flex>
      </template>

      <gaia-form-field label="Target collection" required>
        <gaia-select
          v-model:value="selectedTargetCollection"
          placeholder="Select a collection"
          :options="targetCollectionOptions"
          filterable
          style="width: 100%"
        />
      </gaia-form-field>

      <template #footer>
        <n-flex justify="end" :size="8">
          <gaia-button size="small" @click="showDuplicateModal = false">Cancel</gaia-button>
          <gaia-button
            type="primary"
            size="small"
            :loading="isDuplicating"
            :disabled="!isOrgAdmin || !selectedTargetCollection"
            @click="executeDuplicatePrompt"
          >
            Duplicate
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Full prompt text -->
    <gaia-modal
      :show="showDescriptionModal"
      :style="modalStyleVars"
      width="720px"
      @update:show="(value: boolean) => (showDescriptionModal = value)"
    >
      <template #header>
        <gaia-text size="subtitle" weight="semibold" tone="ink">
          {{ selectedPromptName }}
        </gaia-text>
      </template>

      <n-flex vertical :size="14">
        <pre class="prompt-body">{{ selectedPromptDescription }}</pre>

        <template v-if="selectedPromptExampleData && selectedPromptExampleData.length > 0">
          <gaia-banner plain type="info">
            Example data the model can reference from the prompt.
          </gaia-banner>
          <pre
            v-for="(example, index) in selectedPromptExampleData"
            :key="index"
            class="prompt-body"
            >{{ example }}</pre>
        </template>
      </n-flex>

      <template #footer>
        <n-flex justify="end">
          <gaia-button size="small" @click="showDescriptionModal = false">Close</gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </Layout>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. Rows align to the top: the clamped
   description is the tall cell and everything else reads from its first line. */
.row {
  display: grid;
  align-items: start;
  gap: 14px;
  min-height: 54px;
  padding: 14px 20px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.name-cell {
  gap: 9px;
}

.name-icon {
  flex: none;
  margin-top: 2px;
  color: v-bind(chipColor);
}

/* A prompt's name is an identifier the templates reference, so it takes the
   mono face and wraps rather than truncating. */
.prompt-name {
  min-width: 0;
  font-family: v-bind(monoFont);
  font-size: v-bind(nameSize);
  color: v-bind(nameColor);
  word-break: break-word;
}

/* Long prompt text reads as a preview: three lines, the modal has the rest. */
.clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.6;
}

.options-cell {
  flex-wrap: wrap;
  gap: 5px;
}

.option-chip {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 7px;
  border: 1px solid v-bind(chipBorder);
  border-radius: 6px;
  background: v-bind(chipSurface);
  color: v-bind(chipColor);
  font-size: v-bind(chipTextSize);
  white-space: nowrap;
}

.agents-cell {
  gap: 5px;
}

/* Which agents run — one tinted 22px mark per agent, named by its tooltip. */
.agent-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 22px;
  height: 22px;
  border: 1px solid;
  border-radius: 6px;
}

/* --- modal internals (teleported: tokens arrive via modalStyleVars) --- */
.option-row {
  padding: 10px 12px;
  border: 1px solid var(--pc-border);
  border-radius: 9px;
  background: var(--pc-surface);
}

/* The full prompt as the model sees it — mono, pre-wrapped, scroll-capped. */
.prompt-body {
  margin: 0;
  padding: 12px 14px;
  max-height: 46vh;
  overflow-y: auto;
  border: 1px solid var(--pc-border);
  border-radius: 10px;
  background: var(--pc-surface);
  font-family: var(--pc-mono);
  font-size: var(--pc-mono-size);
  line-height: 1.65;
  color: var(--pc-text);
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
